/**
 * Hook for managing user's theme mode preference (day/night/auto)
 * 
 * This hook:
 * - Stores the user's mode preference in localStorage
 * - Listens for system color scheme changes when in "auto" mode
 * - Provides the effective mode (day or night) based on preference
 * - Is independent of workspace settings (workspace only controls accent colors)
 */

import { useEffect, useState, useCallback } from "react";

// ============================================================================
// Types
// ============================================================================

export type ThemeMode = "day" | "night" | "auto";
export type EffectiveThemeMode = "day" | "night";

export interface UseUserThemeModeReturn {
  /** User's selected mode preference (day/night/auto) */
  mode: ThemeMode;
  /** The actual mode being applied (day or night, resolves "auto" based on system) */
  effectiveMode: EffectiveThemeMode;
  /** Set the user's mode preference */
  setMode: (mode: ThemeMode) => void;
  /** Toggle between day and night (if currently auto, switches to opposite of system) */
  toggleMode: () => void;
  /** Whether the current effective mode is night */
  isNight: boolean;
  /** Whether the current effective mode is day */
  isDay: boolean;
  /** Whether the mode is set to auto */
  isAuto: boolean;
}

// ============================================================================
// Constants
// ============================================================================

const STORAGE_KEY = "crow-user-theme-mode";
const DEFAULT_MODE: ThemeMode = "auto";

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Get the system's preferred color scheme
 */
function getSystemPreference(): EffectiveThemeMode {
  if (typeof window === "undefined") return "day";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "night" : "day";
}

/**
 * Resolve the effective mode from user preference
 */
function resolveEffectiveMode(mode: ThemeMode): EffectiveThemeMode {
  if (mode === "auto") {
    return getSystemPreference();
  }
  return mode;
}

/**
 * Get stored mode from localStorage
 */
function getStoredMode(): ThemeMode {
  if (typeof window === "undefined") return DEFAULT_MODE;
  
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && (stored === "day" || stored === "night" || stored === "auto")) {
      return stored as ThemeMode;
    }
  } catch (e) {
    // localStorage might be unavailable
    console.warn("Failed to read theme mode from localStorage:", e);
  }
  
  return DEFAULT_MODE;
}

/**
 * Store mode in localStorage
 */
function storeMode(mode: ThemeMode): void {
  if (typeof window === "undefined") return;
  
  try {
    localStorage.setItem(STORAGE_KEY, mode);
  } catch (e) {
    console.warn("Failed to store theme mode in localStorage:", e);
  }
}

// ============================================================================
// Main Hook
// ============================================================================

/**
 * Hook to manage user's theme mode preference
 * 
 * @example
 * ```tsx
 * const { mode, effectiveMode, setMode, toggleMode, isNight } = useUserThemeMode();
 * 
 * // Toggle between day/night
 * <button onClick={toggleMode}>
 *   {isNight ? <SunIcon /> : <MoonIcon />}
 * </button>
 * ```
 */
export function useUserThemeMode(): UseUserThemeModeReturn {
  // Initialize state from localStorage
  const [mode, setModeState] = useState<ThemeMode>(() => getStoredMode());
  const [effectiveMode, setEffectiveMode] = useState<EffectiveThemeMode>(() => 
    resolveEffectiveMode(getStoredMode())
  );

  // Update effective mode when mode changes or system preference changes
  useEffect(() => {
    const updateEffectiveMode = () => {
      setEffectiveMode(resolveEffectiveMode(mode));
    };

    updateEffectiveMode();

    // Listen for system preference changes when in auto mode
    if (mode === "auto") {
      const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
      const handleChange = () => updateEffectiveMode();
      
      mediaQuery.addEventListener("change", handleChange);
      return () => mediaQuery.removeEventListener("change", handleChange);
    }
  }, [mode]);

  // Apply theme class to document when effective mode changes
  useEffect(() => {
    if (typeof window === "undefined") return;

    const root = document.documentElement;
    
    // Remove existing theme classes
    root.classList.remove("theme-day", "theme-night");
    
    // Add current mode class
    root.classList.add(`theme-${effectiveMode}`);
  }, [effectiveMode]);

  // Set mode and persist to localStorage
  const setMode = useCallback((newMode: ThemeMode) => {
    setModeState(newMode);
    storeMode(newMode);
  }, []);

  // Toggle between day and night
  const toggleMode = useCallback(() => {
    const newMode: ThemeMode = effectiveMode === "day" ? "night" : "day";
    setMode(newMode);
  }, [effectiveMode, setMode]);

  return {
    mode,
    effectiveMode,
    setMode,
    toggleMode,
    isNight: effectiveMode === "night",
    isDay: effectiveMode === "day",
    isAuto: mode === "auto",
  };
}

// ============================================================================
// Standalone utility for SSR/initial render
// ============================================================================

/**
 * Get the initial theme mode for SSR or before React hydration
 * Can be used in a script tag to prevent flash of wrong theme
 */
export function getInitialThemeMode(): EffectiveThemeMode {
  const stored = getStoredMode();
  return resolveEffectiveMode(stored);
}

export default useUserThemeMode;

